import { useEffect, useMemo, useState } from 'react'
import type { PmmRepository } from '../data/repository'
import { laneMeta } from '../domain/assets'
import type { AssetLane } from '../domain/dailyEngine'
import type { PracticeLibraryItem, PracticePreferenceState } from '../domain/weekly'
import { GuidedAssetContent } from './GuidedAssetContent'

interface PracticeLibraryProps { repository: PmmRepository }

function mutationId(assetId: string, state: string): string {
  return `library-${state}-${assetId}-${crypto.randomUUID()}`
}

const preferenceLabels: Record<PracticePreferenceState, string> = {
  active: 'Available', pinned: 'Pinned', archived: 'Archived', quarantined: 'Paused for safety review',
}

export function PracticeLibrary({ repository }: PracticeLibraryProps) {
  const [items, setItems] = useState<PracticeLibraryItem[]>([])
  const [query, setQuery] = useState('')
  const [lane, setLane] = useState<'all' | AssetLane>('all')
  const [status, setStatus] = useState<'available' | 'pinned' | 'archived'>('available')
  const [selectedId, setSelectedId] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(true)

  useEffect(() => {
    let active = true
    setBusy(true)
    repository.getPracticeLibrary().then((view) => {
      if (!active) return
      setItems(view.items)
      setSelectedId(view.items.find((item) => item.preference !== 'archived')?.asset.assetId ?? '')
      setBusy(false)
    }).catch((error) => {
      if (!active) return
      setNotice(error instanceof Error ? error.message : 'The practice library could not be loaded safely.')
      setBusy(false)
    })
    return () => { active = false }
  }, [repository])

  const filtered = useMemo(() => items.filter((item) => {
    if (lane !== 'all' && item.asset.lane !== lane) return false
    if (status === 'pinned' && item.preference !== 'pinned') return false
    if (status === 'archived' && item.preference !== 'archived') return false
    if (status === 'available' && item.preference === 'archived') return false
    const text = `${item.asset.title} ${item.asset.purpose} ${item.asset.categoryLabel}`.toLowerCase()
    return text.includes(query.trim().toLowerCase())
  }), [items, lane, query, status])
  const selected = items.find((item) => item.asset.assetId === selectedId) ?? filtered[0]

  async function changePreference(item: PracticeLibraryItem, state: 'active' | 'pinned' | 'archived') {
    if (item.preference === 'quarantined') {
      setNotice('This practice is paused after unsafe feedback. It cannot be silently reactivated from the library.')
      return
    }
    setBusy(true)
    try {
      const view = await repository.updatePracticePreference({ assetId: item.asset.assetId, state, clientMutationId: mutationId(item.asset.assetId, state) })
      setItems(view.items)
      setNotice(state === 'pinned' ? 'Pinned for easier return. Safety and cooldown rules still apply to Today.' : state === 'archived' ? 'Archived without judgment. You can restore it later.' : 'Returned to the available library.')
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'That preference could not be saved safely.')
    } finally { setBusy(false) }
  }

  return (
    <section className={`library-workspace ${!busy && items.length === 0 ? 'is-waiting' : ''}`} aria-labelledby="library-title">
      <header className="journey-hero compact-hero">
        <div><p className="eyebrow">Practice library</p><h1 id="library-title">Keep what serves.<br /><em>Reduce what does not.</em></h1><p>Search the approved library, pin useful practices and archive noise. History is descriptive, never a score.</p></div>
        <div className="library-stats"><strong>{items.length}</strong><span>approved practices</span><small>{items.filter((item) => item.preference === 'pinned').length} pinned</small></div>
      </header>

      <div className="library-controls" aria-label="Practice filters">
        <label><span>Search</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search title, purpose or category" /></label>
        <label><span>Lane</span><select value={lane} onChange={(event) => setLane(event.target.value as 'all' | AssetLane)}><option value="all">All lanes</option><option value="self">Self development</option><option value="relationship">Relationship development</option><option value="fatherhood">Fatherhood & family</option></select></label>
        <div className="status-tabs">{(['available', 'pinned', 'archived'] as const).map((value) => <button type="button" key={value} className={status === value ? 'is-active' : ''} onClick={() => setStatus(value)}>{value[0].toUpperCase() + value.slice(1)}</button>)}</div>
      </div>

      {busy && items.length === 0 && <div className="locked-review"><span>Loading your library</span><h3>Keeping private history and current preferences separate.</h3></div>}
      {!busy && items.length === 0 && <section className="personal-coaching-waiting" aria-labelledby="practice-waiting-title">
        <div className="waiting-mark" aria-hidden="true"><span /><span /><span /></div>
        <p className="eyebrow">Your personal practice library</p>
        <h2 id="practice-waiting-title">Waiting for your Coach to publish your first personal practices.</h2>
        <p>This space is intentionally empty. A generic library would look complete, but it would not know you. Your library will open only after Coach has reviewed and published guidance created from information you chose to share.</p>
        <div className="waiting-boundaries">
          <span><strong>No filler</strong>Generic practices do not appear here</span>
          <span><strong>Your pace</strong>Honest capacity shapes what is selected</span>
          <span><strong>Last-good protection</strong>Published personal guidance stays through generation failures</span>
        </div>
      </section>}
      <div className="library-layout">
        <div className="library-list" role="list">{filtered.map((item) => <button type="button" role="listitem" key={item.asset.assetId} className={selected?.asset.assetId === item.asset.assetId ? 'is-active' : ''} onClick={() => setSelectedId(item.asset.assetId)}><span>{laneMeta[item.asset.lane].label} · {item.asset.durationMinutes} min</span><strong>{item.asset.title}</strong><small>{preferenceLabels[item.preference]} · {item.stats.completed} actions recorded</small></button>)}</div>
        {selected && <article className="library-detail">
          <div className="library-detail-top"><div><p className="eyebrow">{selected.asset.categoryLabel}</p><h2>{selected.asset.title}</h2><p>{selected.asset.purpose}</p></div><span className={`preference-state state-${selected.preference}`}>{preferenceLabels[selected.preference]}</span></div>
          <div className="library-history" aria-label="Practice history"><div><strong>{selected.stats.attempted}</strong><span>attempted</span></div><div><strong>{selected.stats.completed}</strong><span>actions done</span></div><div><strong>{selected.stats.helpful}</strong><span>explicitly helpful</span></div><div><strong>{selected.stats.poorFit}</strong><span>poor fit</span></div></div>
          <ol className="practice-steps">{selected.asset.instructions.map((step, index) => <li key={step}><span>{String(index + 1).padStart(2, '0')}</span><p>{step}</p></li>)}</ol>
          <GuidedAssetContent asset={selected.asset} />
          <p className="completion-signal"><strong>Action occurred when:</strong> {selected.asset.completionSignal} This does not prove benefit.</p>
          <footer className="practice-actions"><button type="button" className="quiet-action" disabled={busy || selected.preference === 'quarantined'} onClick={() => void changePreference(selected, selected.preference === 'pinned' ? 'active' : 'pinned')}>{selected.preference === 'pinned' ? 'Unpin' : 'Pin practice'}</button><button type="button" className="quiet-action" disabled={busy || selected.preference === 'quarantined'} onClick={() => void changePreference(selected, selected.preference === 'archived' ? 'active' : 'archived')}>{selected.preference === 'archived' ? 'Restore' : 'Archive'}</button></footer>
        </article>}
      </div>
      {items.length > 0 && filtered.length === 0 && !busy && <div className="empty-library"><h3>No practices match this view.</h3><p>Change a filter. Nothing has been deleted.</p></div>}
      {notice && <div className="toast" role="status"><span />{notice}<button type="button" onClick={() => setNotice('')} aria-label="Dismiss message">×</button></div>}
    </section>
  )
}
